/**
 * ProductsListV2.jsx
 * 
 * Unified product listing page for V2 template-based products.
 * Shows products filtered by type (LGF, SMF, AWD, PROMO) with
 * search, filtering, and grid/list view options.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { 
  Loader2, 
  Grid, 
  List, 
  Package, 
  Search,
  Filter,
  X,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { productsV2API } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Product type configuration
const PRODUCT_TYPES = {
  LGF: {
    label: 'Large Format',
    description: 'Banners, Signs, Posters & More',
    color: 'bg-blue-500',
    icon: '🖼️'
  },
  SMF: {
    label: 'Small Format',
    description: 'Business Cards, Flyers, Brochures',
    color: 'bg-green-500',
    icon: '📄'
  },
  AWD: {
    label: 'Awards & Recognition',
    description: 'Crystal, Glass & Acrylic Awards',
    color: 'bg-purple-500',
    icon: '🏆'
  },
  PROMO: {
    label: 'Promotional Products',
    description: 'Custom Branded Merchandise',
    color: 'bg-orange-500',
    icon: '🎁'
  }
};

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  if (imageUrl.startsWith('/')) {
    return `${API_URL}${imageUrl}`;
  }
  return imageUrl;
};

// Product Card Component
const ProductCard = ({ product, viewMode = 'grid' }) => {
  const imageUrl = getImageUrl(product.images?.primary) || 
    `https://placehold.co/400x400/f5f3f0/9a8b7d?text=${encodeURIComponent(product.name?.substring(0, 10) || 'Product')}`;
  
  const typeConfig = PRODUCT_TYPES[product.product_type] || PRODUCT_TYPES.LGF;
  
  if (viewMode === 'list') {
    return (
      <Link to={`/v2/product/${product.id}`} className="block">
        <Card className="hover:shadow-lg transition-shadow bg-ivory-100 border-ivory-300/50">
          <CardContent className="p-4 flex gap-4">
            <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-white">
              <img
                src={imageUrl}
                alt={product.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.src = `https://placehold.co/400x400/f5f3f0/9a8b7d?text=Product`;
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary" className="text-xs">
                  {typeConfig.label}
                </Badge>
                {product.status === 'active' && (
                  <Badge className="bg-green-100 text-green-700 text-xs">In Stock</Badge>
                )}
              </div>
              <h3 className="font-heading font-semibold text-lg text-ivory-800 truncate">
                {product.name}
              </h3>
              <p className="text-sm text-ivory-500 line-clamp-2 mt-1">
                {product.descriptions?.short || 'Premium quality printing'}
              </p>
              {product.sku && (
                <p className="text-xs text-ivory-400 mt-2 font-mono">SKU: {product.sku}</p>
              )}
            </div>
            <div className="flex flex-col items-end justify-between">
              <ChevronRight className="w-5 h-5 text-ivory-400" />
              {product.tags?.length > 0 && (
                <div className="flex gap-1">
                  {product.tags.slice(0, 2).map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }
  
  // Grid view
  return (
    <Link to={`/v2/product/${product.id}`} className="block group">
      <Card className="hover:shadow-lg transition-all hover:-translate-y-1 bg-ivory-100 border-ivory-300/50 overflow-hidden h-full">
        <div className="aspect-square bg-white relative overflow-hidden">
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
            onError={(e) => {
              e.target.src = `https://placehold.co/400x400/f5f3f0/9a8b7d?text=Product`;
            }}
          />
          <div className="absolute top-2 left-2">
            <Badge variant="secondary" className="text-xs shadow">
              {typeConfig.icon} {typeConfig.label}
            </Badge>
          </div>
          {product.status === 'active' && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-green-500 text-white text-xs shadow">In Stock</Badge>
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <h3 className="font-heading font-semibold text-ivory-800 line-clamp-2 min-h-[3rem]">
            {product.name}
          </h3>
          <p className="text-sm text-ivory-500 mt-2 line-clamp-2">
            {product.descriptions?.short || 'Premium quality printing'}
          </p>
          {product.sku && (
            <p className="text-xs text-ivory-400 mt-2 font-mono">SKU: {product.sku}</p>
          )}
          {product.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {product.tags.slice(0, 3).map((tag, i) => (
                <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

// Main Component
const ProductsListV2 = () => {
  const { productType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedTemplate, setSelectedTemplate] = useState(searchParams.get('template') || 'all');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'active');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name_asc');
  
  // Get current product type config
  const currentTypeConfig = productType ? PRODUCT_TYPES[productType.toUpperCase()] : null;
  const normalizedType = productType?.toUpperCase();

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {
          status: selectedStatus === 'all' ? undefined : selectedStatus,
          search: searchQuery || undefined,
          limit: 50
        };
        
        if (normalizedType) {
          params.product_type = normalizedType;
        }
        
        if (selectedTemplate && selectedTemplate !== 'all') {
          params.template_id = selectedTemplate;
        }
        
        const response = await productsV2API.getProducts(params);
        setProducts(response.products || []);
        setTotalProducts(response.total || 0);
        
        // Also fetch templates for filter
        const templatesResponse = await productsV2API.getTemplates({
          product_type: normalizedType,
          status: 'active'
        });
        setTemplates(templatesResponse.templates || []);
        
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [normalizedType, selectedTemplate, selectedStatus, searchQuery]);

  // Sort products
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'name_asc':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name_desc':
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      default:
        break;
    }
    return sorted;
  }, [products, sortBy]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTemplate('all');
    setSelectedStatus('active');
    setSortBy('name_asc');
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || selectedTemplate !== 'all' || selectedStatus !== 'active';

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Hero Section */}
      <div className={`${currentTypeConfig?.color || 'bg-gold-600'} text-white py-12`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm mb-4 opacity-80">
            <Link to="/" className="hover:underline">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/v2/products" className="hover:underline">Products</Link>
            {currentTypeConfig && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span>{currentTypeConfig.label}</span>
              </>
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            {currentTypeConfig ? (
              <>{currentTypeConfig.icon} {currentTypeConfig.label}</>
            ) : (
              '🛍️ All Products'
            )}
          </h1>
          <p className="text-xl opacity-90 max-w-2xl">
            {currentTypeConfig?.description || 'Browse our complete catalog of premium printing and engraving products'}
          </p>
        </div>
      </div>

      {/* Product Type Tabs (when viewing all) */}
      {!productType && (
        <div className="bg-white border-b border-ivory-200 sticky top-0 z-10">
          <div className="container mx-auto px-4">
            <div className="flex gap-2 py-4 overflow-x-auto">
              <Link to="/v2/products">
                <Button 
                  variant={!productType ? "default" : "outline"}
                  className="whitespace-nowrap"
                >
                  All Products
                </Button>
              </Link>
              {Object.entries(PRODUCT_TYPES).map(([type, config]) => (
                <Link key={type} to={`/v2/products/${type.toLowerCase()}`}>
                  <Button variant="outline" className="whitespace-nowrap">
                    {config.icon} {config.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-ivory-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-ivory-50 border-ivory-300"
                  data-testid="search-input"
                />
              </div>
            </form>

            {/* Template Filter */}
            {templates.length > 0 && (
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="w-[180px] bg-ivory-50 border-ivory-300" data-testid="template-filter">
                  <SelectValue placeholder="All Templates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Templates</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px] bg-ivory-50 border-ivory-300" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name A-Z</SelectItem>
                <SelectItem value="name_desc">Name Z-A</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <div className="flex border border-ivory-300 rounded-lg overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-none"
                data-testid="grid-view-btn"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Results Count */}
          <div className="mt-3 text-sm text-ivory-500">
            Showing {sortedProducts.length} of {totalProducts} products
            {searchQuery && <span> matching "{searchQuery}"</span>}
          </div>
        </div>

        {/* Products Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-gold-600 animate-spin" />
          </div>
        ) : sortedProducts.length === 0 ? (
          <Card className="bg-ivory-100 border-ivory-300/50">
            <CardContent className="py-16 text-center">
              <Package className="w-16 h-16 text-ivory-300 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold text-ivory-700 mb-2">
                No Products Found
              </h3>
              <p className="text-ivory-500 mb-4">
                {searchQuery 
                  ? `No products match your search "${searchQuery}"`
                  : 'No products available in this category yet'}
              </p>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="outline">
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} viewMode="grid" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} viewMode="list" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsListV2;
